/**
 * Whether the game stage is on screen.
 *
 * Each game page is the game first, with the guide below it. Once a player
 * scrolls down to read, the board is off screen, and two things must follow:
 * a real-time game pauses instead of being lost unseen, and the keyboard goes
 * back to scrolling the page instead of steering a game nobody can see.
 *
 * The stage counts as visible while at least half of it is on screen, and
 * always while immersive (fullscreen) mode is on, since the stage then covers
 * the viewport.
 */

const VISIBLE_SHARE = 0.5;

let stageVisible = true;
const hiddenListeners: Array<() => void> = [];

function observe(): void {
  const stage = document.getElementById('stage');
  if (!stage || !('IntersectionObserver' in window)) return;

  const observer = new IntersectionObserver(
    (entries) => {
      const entry = entries[entries.length - 1];
      const visible = entry.intersectionRatio >= VISIBLE_SHARE;
      if (stageVisible && !visible) {
        stageVisible = false;
        if (!isImmersive()) hiddenListeners.forEach((listener) => listener());
      } else if (visible) {
        stageVisible = true;
      }
    },
    { threshold: [0, VISIBLE_SHARE, 1] },
  );
  observer.observe(stage);
}

function isImmersive(): boolean {
  return document.body.classList.contains('immersive');
}

export function isStageInView(): boolean {
  return stageVisible || isImmersive();
}

/** Calls `listener` each time the stage scrolls out of view. */
export function onStageHidden(listener: () => void): void {
  hiddenListeners.push(listener);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', observe, { once: true });
} else {
  observe();
}
